import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  apiFetch,
  buildApiUrl,
  createApiHeaders,
} from '../../../../config/api';
import { debugError } from '../../../../utils/debugLog';

/**
 * Custom hook for ContentCheckAuditDashboard component
 *
 * Manages state and data fetching for the audit dashboard.
 *
 * @returns {Object} Hook return object with state and functions
 */
export const useContentCheckAudit = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [referenceTypeFilter, setReferenceTypeFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [dateRange] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [userFilterAnchor, setUserFilterAnchor] = useState(null);

  // Sorting
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        ...(debouncedSearch && debouncedSearch.length >= 3
          ? { search: debouncedSearch }
          : {}),
        ...(referenceTypeFilter && { reference_type: referenceTypeFilter }),
        ...(severityFilter && { severity: severityFilter }),
        ...(dateRange && { date_range: dateRange }),
        ...(userFilter && { user_id: userFilter }),
      });

      const response = await apiFetch(
        `/api/v1/content-check-audit/logs?${params}`
      );

      if (response && response.success) {
        setAuditLogs(response.logs || []);
        setTotalPages(response.total_pages || 1);
        setTotalCount(response.total_count || 0);
      } else {
        const errorMessage =
          response?.message || response?.detail || 'Failed to fetch audit logs';
        setError(errorMessage);
        debugError('Audit logs API returned unsuccessful response:', {
          success: response?.success,
          message: response?.message,
          detail: response?.detail,
          response,
        });
      }
    } catch (err) {
      const errorMessage =
        err?.message ||
        err?.response?.data?.detail ||
        'Failed to fetch audit logs';
      setError(errorMessage);
      debugError('Audit logs fetch error:', {
        error: err,
        message: err?.message,
        response: err?.response?.data,
        stack: err?.stack,
      });
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    debouncedSearch,
    referenceTypeFilter,
    severityFilter,
    dateRange,
    userFilter,
  ]);

  useEffect(() => {
    // Only fetch when search is empty or at least 3 characters
    if (
      debouncedSearch &&
      debouncedSearch.length > 0 &&
      debouncedSearch.length < 3
    ) {
      return;
    }
    fetchAuditLogs();
  }, [
    page,
    pageSize,
    debouncedSearch,
    referenceTypeFilter,
    severityFilter,
    dateRange,
    userFilter,
    fetchAuditLogs,
  ]);

  const handleViewDetails = log => {
    setSelectedLog(log);
    setDetailsOpen(true);
  };

  const handleExport = async (format = 'csv') => {
    try {
      const params = new URLSearchParams({ format });
      if (referenceTypeFilter)
        params.append('reference_type', referenceTypeFilter);
      if (severityFilter) params.append('severity', severityFilter);
      if (debouncedSearch && debouncedSearch.length >= 3) {
        params.append('search', debouncedSearch);
      }
      const apiUrl = buildApiUrl(
        `/api/v1/content-check-audit/export?${params.toString()}`
      );
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: createApiHeaders(),
      });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      const disp = res.headers.get('Content-Disposition') || '';
      const match = disp.match(/filename=([^;]+)/i);
      a.download = match ? match[1] : `content-check-audit.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      debugError('Export error:', err);
    }
  };

  // Get unique users from audit logs (user_id field contains user email)
  const uniqueUsers = useMemo(() => {
    const users = new Set();
    auditLogs.forEach(log => {
      if (log.user_id) {
        users.add(log.user_id); // user_id field contains user email
      }
    });
    return Array.from(users).sort();
  }, [auditLogs]);

  // Handle sorting
  const handleSort = column => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to descending for timestamp
      setSortBy(column);
      setSortOrder(column === 'timestamp' ? 'desc' : 'asc');
    }
  };

  // Sort audit logs client-side
  const sortedAuditLogs = useMemo(() => {
    if (!auditLogs.length) return [];

    const sorted = [...auditLogs].sort((a, b) => {
      let aValue, bValue;

      if (sortBy === 'timestamp') {
        aValue = new Date(a.timestamp || a.created_at || 0);
        bValue = new Date(b.timestamp || b.created_at || 0);
      } else {
        return 0;
      }

      // Compare values
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [auditLogs, sortBy, sortOrder]);

  // Handle user filter
  const handleUserFilterOpen = event => {
    setUserFilterAnchor(event.currentTarget);
  };

  const handleUserFilterClose = () => {
    setUserFilterAnchor(null);
  };

  const handleUserFilterSelect = userId => {
    setUserFilter(userId === 'all' ? '' : userId);
    setPage(1); // Reset to first page when filter changes
    handleUserFilterClose();
  };

  return {
    // State
    auditLogs,
    loading,
    error,
    selectedLog,
    detailsOpen,
    page,
    pageSize,
    totalPages,
    totalCount,
    searchTerm,
    referenceTypeFilter,
    severityFilter,
    userFilter,
    userFilterAnchor,
    sortBy,
    sortOrder,
    uniqueUsers,
    sortedAuditLogs,

    // Actions
    setSearchTerm,
    setReferenceTypeFilter,
    setSeverityFilter,
    setPageSize,
    setPage,
    setDetailsOpen,
    fetchAuditLogs,
    handleViewDetails,
    handleExport,
    handleSort,
    handleUserFilterOpen,
    handleUserFilterClose,
    handleUserFilterSelect,
  };
};
